import { useEffect, useState } from "react";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiDownload,
  FiFileText,
  FiPrinter,
  FiTruck,
  FiUser,
  FiXCircle,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const date = (value) =>
  value
    ? new Intl.DateTimeFormat("en-IN", { dateStyle: "medium" }).format(
        new Date(value),
      )
    : "—";

export default function DeliveryChallanDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { accessToken } = useAuth();
  const [challan, setChallan] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setChallan(await apiRequest(`/delivery-challans/${id}`, { accessToken }));
    } catch (err) {
      setError(err.message || "Unable to load delivery challan.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) load();
  }, [accessToken, id]);

  const dispatch = async () => {
    try {
      setActing(true);
      await apiRequest(`/delivery-challans/${id}/dispatch`, {
        method: "POST",
        accessToken,
      });
      await load();
    } catch (err) {
      setError(err.message || "Unable to dispatch challan.");
    } finally {
      setActing(false);
    }
  };

  const cancel = async () => {
    const reason = window.prompt("Enter cancellation reason:");
    if (!reason?.trim()) return;

    try {
      setActing(true);
      await apiRequest(`/delivery-challans/${id}/cancel`, {
        method: "POST",
        accessToken,
        body: { reason: reason.trim() },
      });
      await load();
    } catch (err) {
      setError(err.message || "Unable to cancel challan.");
    } finally {
      setActing(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-primary-100 border-t-primary-700 animate-spin" />
      </div>
    );
  if (!challan)
    return (
      <div className="rounded-2xl bg-red-50 p-6 text-red-700">{error}</div>
    );

  const quantity = challan.items.reduce(
    (sum, item) => sum + Number(item.quantityDispatched || 0),
    0,
  );
  const dispatched = challan.status === "DISPATCHED";

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:justify-between">
        <div>
          <button
            onClick={() => navigate("/admindashboard/delivery/challan/list")}
            className="flex items-center gap-2 text-gray-500 hover:text-primary-700 mb-4"
          >
            <FiArrowLeft /> Back to Delivery Challans
          </button>
          <div className="flex gap-4 items-center">
            <h1 className="text-4xl font-bold">
              {challan.deliveryChallanNumber}
            </h1>
            <Badge status={challan.status} />
          </div>
          <p className="text-gray-500 mt-3">Delivery Challan Details</p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => window.print()}
            className="h-11 px-5 rounded-xl border border-gray-200 flex gap-2 items-center"
          >
            <FiPrinter /> Print
          </button>
          <button
            onClick={() => window.print()}
            className="h-11 px-5 rounded-xl border border-gray-200 flex gap-2 items-center"
          >
            <FiDownload /> PDF
          </button>
          {challan.status === "DRAFT" && (
            <button
              disabled={acting}
              onClick={dispatch}
              className="h-11 px-6 rounded-xl bg-primary-700 text-white flex gap-2 items-center disabled:opacity-60"
            >
              <FiTruck /> Dispatch
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
          {error}
        </div>
      )}

      <div className="grid xl:grid-cols-2 gap-6">
        <Card icon={<FiUser />} title="Customer Information">
          <Info
            label="Customer"
            value={challan.salesOrder?.customerNameSnapshot}
          />
          <Info
            label="Sales Order"
            value={challan.salesOrder?.salesOrderNumber}
          />
          <Info label="Store" value={challan.store?.name} />
          <Info label="Delivery Date" value={date(challan.deliveryDate)} />
        </Card>

        <Card icon={<FiTruck />} title="Dispatch Information">
          <Info label="Transporter" value={challan.transporter} />
          <Info label="Vehicle Number" value={challan.vehicleNumber} />
          <Info
            label="Created By"
            value={
              challan.createdBy?.employee?.fullName || challan.createdBy?.email
            }
          />
          <Info label="Dispatched At" value={date(challan.dispatchedAt)} />
        </Card>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 p-8">
        <div className="flex justify-between">
          <div>
            <h2 className="text-2xl font-bold">Linked Sales Order</h2>
            <p className="text-gray-500 mt-2">
              Source order for this dispatch.
            </p>
          </div>
          <button
            onClick={() =>
              navigate(`/admindashboard/sales/orders/${challan.salesOrder?.id}`)
            }
            className="h-11 px-5 rounded-xl border border-primary-700 text-primary-700"
          >
            View Sales Order
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b">
          <h2 className="text-2xl font-bold">Dispatched Items</h2>
          <p className="text-gray-500 mt-1">
            {challan.items.length} items · {quantity} units
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[750px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left">Product</th>
                <th className="px-6 py-4 text-left">SKU</th>
                <th className="px-6 py-4 text-left">Variant</th>
                <th className="px-6 py-4 text-left">Reserved Lots</th>
                <th className="px-6 py-4 text-center">Quantity</th>
                <th className="px-6 py-4 text-right">Unit Price</th>
              </tr>
            </thead>
            <tbody>
              {challan.items.map((item) => (
                <tr key={item.id} className="border-b">
                  <td className="px-6 py-5 font-semibold">
                    {item.productNameSnapshot}
                  </td>
                  <td className="px-6 py-5">{item.skuSnapshot}</td>
                  <td className="px-6 py-5">
                    {item.variantNameSnapshot || "Default"}
                  </td>
                  <td className="px-6 py-5">
                    <LotDispatchSummary
                      allocations={item.salesOrderItem?.lotAllocations}
                    />
                  </td>
                  <td className="px-6 py-5 text-center">
                    {Number(item.quantityDispatched)}
                  </td>
                  <td className="px-6 py-5 text-right">
                    ₹{Number(item.unitPrice).toLocaleString("en-IN")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {challan.notes && (
        <div className="bg-white rounded-3xl border border-gray-100 p-6">
          <h2 className="text-xl font-bold mb-3">Notes</h2>
          <p className="text-gray-600">{challan.notes}</p>
        </div>
      )}

      {challan.status === "DRAFT" && (
        <button
          disabled={acting}
          onClick={cancel}
          className="h-11 px-6 rounded-xl border border-red-200 text-red-600 flex gap-2 items-center"
        >
          <FiXCircle /> Cancel Draft
        </button>
      )}
      {dispatched && (
        <div className="rounded-2xl bg-green-50 border border-green-200 p-5 text-green-800 flex gap-3">
          <FiCheckCircle size={22} /> Stock has been issued and the Sales Order
          quantities were updated.
        </div>
      )}
    </div>
  );
}

function Card({ icon, title, children }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-8">
      <div className="flex items-center gap-3 mb-6 text-primary-700">
        {icon}
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
      </div>
      {children}
    </div>
  );
}
function Info({ label, value }) {
  return (
    <div className="flex justify-between gap-5 py-3 border-b border-gray-100 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-right">{value || "—"}</span>
    </div>
  );
}
function Badge({ status }) {
  const styles = {
    DRAFT: "bg-gray-100 text-gray-700",
    DISPATCHED: "bg-green-100 text-green-700",
    CANCELLED: "bg-red-100 text-red-700",
  };
  return (
    <span className={`px-4 py-2 rounded-full font-semibold ${styles[status]}`}>
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

function LotDispatchSummary({ allocations = [] }) {
  if (!allocations.length) {
    return <span className="text-sm text-gray-400">Legacy / not allocated</span>;
  }

  return (
    <div className="space-y-2 text-xs">
      {allocations.map((allocation) => {
        const lot = allocation.inventoryLot;
        const issued = Number(allocation.quantityIssued || 0);

        return (
          <div key={allocation.id} className="rounded-xl bg-gray-50 px-3 py-2">
            <p className="font-semibold text-gray-800">
              {lot?.lotNumber || "Lot unavailable"} · {Number(allocation.quantity)} reserved
            </p>
            <p className="mt-1 text-gray-500">
              Issued {issued} · Cost ₹{Number(lot?.unitCost || 0).toLocaleString("en-IN")}
            </p>
          </div>
        );
      })}
    </div>
  );
}
